import { ReactNode } from "react";

import LevelZeroIcon from "@assets/icons/sourcing-theme-level-zero.svg";
import LevelOneIcon from "@assets/icons/sourcing-theme-level-one.svg";
import LevelThreeIcon from "@assets/icons/sourcing-theme-level-three.svg";
import LevelTwoIcon from "@assets/icons/sourcing-theme-level-two.svg";
import { cn } from "@utils/tailwind.util";

export interface IThemeBadgeProps {
  level: number;
  title?: string;
  // Renders in place of `title` (e.g. a search-highlighted title); falls back to `title`.
  titleSlot?: ReactNode;
  size?: "small" | "medium";
  containerClassName?: string;
  onlyIcon?: boolean;
  isDisabled?: boolean;
  isLevelOneBased?: boolean;
}

export function ThemeBadge({
  level,
  title,
  titleSlot,
  size = "small",
  containerClassName,
  onlyIcon = false,
  isDisabled = false,
  isLevelOneBased = false,
}: IThemeBadgeProps) {
  const themeLevel = isLevelOneBased ? level - 1 : level;

  let Icon;
  switch (themeLevel) {
    case 0:
      Icon = LevelZeroIcon;
      break;
    case 1:
      Icon = LevelOneIcon;
      break;
    case 2:
      Icon = LevelTwoIcon;
      break;
    case 3:
      Icon = LevelThreeIcon;
      break;
    default:
      Icon = LevelThreeIcon;
      break;
  }

  let sizeClassNames = "";
  let iconSize = 16;
  let textClassName = "text-xs";
  switch (size) {
    case "small":
      sizeClassNames = "px-1 py-0.5 rounded-[5px]";
      iconSize = 16;
      textClassName = "text-xs";
      break;
    case "medium":
      sizeClassNames = "pl-1 pr-2 py-1 rounded-lg";
      iconSize = 20;
      textClassName = "text-sm";
      break;
  }

  if (onlyIcon) {
    return <Icon width={iconSize} height={iconSize} className="flex-shrink-0" />;
  }

  return (
    <div
      className={cn(
        "flex flex-1 min-w-0 items-center gap-1 bg-background-tag dark:bg-gray-750 text-content-primary dark:text-white cursor-pointer",
        { "cursor-not-allowed": isDisabled },
        sizeClassNames,
        containerClassName
      )}
    >
      {Icon && (
        <Icon
          width={iconSize}
          height={iconSize}
          className="flex-shrink-0 dark:text-invertedPrimary80"
        />
      )}
      {(titleSlot || title) && (
        <p
          className={cn(
            "truncate font-medium text-gray-950 dark:text-white cursor-pointer",
            { "cursor-not-allowed": isDisabled },
            textClassName
          )}
        >
          {titleSlot ?? title}
        </p>
      )}
    </div>
  );
}
